import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { TextField } from "@mui/material";

import GoogleMaps from "../../../../../GoogleMaps";
import Card from "../../../../../Card";
import { UIFieldShell } from "../../../../../ui/form";
import { getAddressFromLatLng } from "../../../../../../utils/Geocode";
import { clx } from "../../../../../../utils/Helpers";

import "./LocationBoundaries.scss";

const DEFAULT_LOCATION = {
  address: "",
  lat: "",
  lng: "",
  radius: "",
  city_id: null,
};
const DEFAULT_MAP_COORDS = {
  coords: {
    lat: 24.7136,
    lng: 46.6753,
  },
};
const DEFAULT_LABELS = {
  title: "Location",
  eventLocation: "Event Location",
  emptyAddress: "Select location on map",
  boundaries: "Location Boundaries",
  boundariesPlaceholder: "1000",
  radiusFromCenter: "Radius from center point",
};

const normalizeLocation = (location) => ({
  ...DEFAULT_LOCATION,
  ...(location || {}),
});

const hasCoords = (location) => Boolean(location.lat && location.lng);

const LocationBoundaries = ({
  value,
  onChange,
  disabled,
  /** When true, pin / map cannot move; radius may still be editable. */
  mapInteractionsDisabled,
  /** When true, radius field is disabled (map may still be movable). */
  radiusFieldDisabled,
  /** Visual-only map center to fall back to before any location is picked (e.g. selected city). */
  suggestedCenter,
  required,
  locationError,
  radiusError,
  labels,
  hasError,
}) => {
  const mapLocked = Boolean(disabled || mapInteractionsDisabled);
  const radiusLocked = Boolean(disabled || radiusFieldDisabled);

  const location = useMemo(() => normalizeLocation(value), [value]);
  const labelText = useMemo(() => ({ ...DEFAULT_LABELS, ...labels }), [labels]);
  const [markerTouched, setMarkerTouched] = useState(false);
  const reverseGeocodedCoords = useRef("");
  const reverseGeocodeRequestId = useRef(0);
  /** Lets the async reverse-geocode callback read the current value, not the one from the drag. */
  const latestRef = useRef({ location, onChange });
  latestRef.current = { location, onChange };

  const updateLocation = useCallback(
    (updates) => {
      onChange?.({
        ...location,
        ...updates,
      });
    },
    [location, onChange],
  );

  const updateAddressFromCoords = useCallback((lat, lng) => {
    reverseGeocodedCoords.current = `${lat},${lng}`;
    const requestId = ++reverseGeocodeRequestId.current;
    const applyAddress = (address) => {
      // A newer drag started meanwhile; its own response owns the address.
      if (requestId !== reverseGeocodeRequestId.current) return;
      const { location: current, onChange: emit } = latestRef.current;
      emit?.({ ...current, address, lat, lng });
    };
    getAddressFromLatLng(
      lat,
      lng,
      (address) => applyAddress(address || ""),
      () => applyAddress(""),
    );
  }, []);

  const handleMarkerMove = useCallback(
    (position) => {
      if (!position?.coords) return;

      const { lat, lng } = position.coords;
      // Commit the coordinates right away so re-renders can't snap the pin back
      // while the address is still being looked up.
      updateLocation({ lat, lng });
      updateAddressFromCoords(lat, lng);
    },
    [updateLocation, updateAddressFromCoords],
  );

  useEffect(() => {
    const coordsKey = `${location.lat},${location.lng}`;

    if (
      hasCoords(location) &&
      !location.address &&
      reverseGeocodedCoords.current !== coordsKey
    ) {
      updateAddressFromCoords(location.lat, location.lng);
    }
  }, [location.address, location.lat, location.lng, updateAddressFromCoords]);

  const locationErrorText =
    typeof locationError === "string" ? locationError : "";
  const radiusErrorText = typeof radiusError === "string" ? radiusError : "";

  return (
    <Card
      title={labelText.title}
      classNames={{
        root: clx("location-boundaries", {
          "form-section--error": hasError,
        }),
        body: "location-boundaries__body",
      }}
    >
      <div className="location-boundaries__map">
        <GoogleMaps
          getCoords={handleMarkerMove}
          className="location-boundaries__map-canvas"
          isMarker
          showCircle={Boolean(location.radius)}
          circleRadius={Number(location.radius) || 0}
          wrapperClasses="location-boundaries__map-wrapper"
          markerMoved={() => setMarkerTouched(true)}
          updatedPosition={
            hasCoords(location)
              ? { coords: { lat: location.lat, lng: location.lng } }
              : suggestedCenter
              ? { coords: suggestedCenter }
              : undefined
          }
          savedCoords={
            hasCoords(location)
              ? { coords: { lat: location.lat, lng: location.lng } }
              : suggestedCenter
              ? { coords: suggestedCenter }
              : DEFAULT_MAP_COORDS
          }
          viewOnly={mapLocked}
        />

      </div>

      <div className="location-boundaries__details">
        <div className="location-boundaries__event-location">
          <div className="location-boundaries__event-location-label">
            {labelText.eventLocation}
          </div>
          <div className="location-boundaries__event-location-value">
            {location.address || labelText.emptyAddress}
          </div>
          {(locationErrorText ||
            (required && markerTouched && !hasCoords(location))) && (
            <div className="location-boundaries__error">
              {locationErrorText || labelText.emptyAddress}
            </div>
          )}
        </div>

        <div className="location-boundaries__boundaries">
          <UIFieldShell
          id="eventRadius"
          label={labelText.boundaries}
          error={Boolean(radiusErrorText)}
          helperText={radiusErrorText || labelText.radiusFromCenter}
          required={required}
        >
          <TextField
            id="eventRadius"
            name="eventRadius"
            type="number"
            placeholder={labelText.boundariesPlaceholder}
            value={location.radius}
            error={Boolean(radiusErrorText)}
            disabled={radiusLocked}
            helperText={null}
            onBlur={() =>
              onChange?.({ ...location, radius: location.radius })
            }
            onChange={(event) =>
              updateLocation({ radius: event.target.value })
            }
          />
        </UIFieldShell>
        </div>
      </div>
    </Card>
  );
};

LocationBoundaries.propTypes = {
  mapInteractionsDisabled: PropTypes.bool,
  radiusFieldDisabled: PropTypes.bool,
  value: PropTypes.shape({
    address: PropTypes.string,
    lat: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    lng: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    radius: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    city_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  suggestedCenter: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  locationError: PropTypes.string,
  radiusError: PropTypes.string,
  labels: PropTypes.shape({
    title: PropTypes.string,
    eventLocation: PropTypes.string,
    emptyAddress: PropTypes.string,
    boundaries: PropTypes.string,
    boundariesPlaceholder: PropTypes.string,
    radiusFromCenter: PropTypes.string,
  }),
  hasError: PropTypes.bool,
};

LocationBoundaries.defaultProps = {
  value: DEFAULT_LOCATION,
  onChange: undefined,
  disabled: false,
  mapInteractionsDisabled: false,
  radiusFieldDisabled: false,
  suggestedCenter: null,
  required: false,
  locationError: "",
  radiusError: "",
  labels: DEFAULT_LABELS,
  hasError: false,
};

export default LocationBoundaries;
